// @owners { team: patient-health }
import { ActionSheetContext, ActionSheetV2 } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useContext, useEffect } from 'react';
import { ASSISTANT_VIEWED_EVENTS } from '~shared/features/alto-assistant/analytics/constants';
import {
  sendAssistantUpcomingDeliveryActionChosen,
  sendAssistantViewedEvent,
} from '~shared/features/alto-assistant/analytics/helpers';
import {
  ASSISTANT_CHAT_ORIGINS,
  ASSISTANT_DELIVERY_ACTIONS,
  ASSISTANT_MESSAGE_SUBJECTS,
  type ActionSheetProviderStackCacheKey,
  type AssistantCategory,
  type AssistantDeliveryAction,
  type AssistantMessageSubject,
  MESSAGE_PROMPTS,
} from '~shared/features/alto-assistant/constants';
import { getItemMedNames } from '~shared/features/checkout/helpers';
import isShipmentEditable from '~shared/features/shipments/helpers/isShipmentEditable';
import { type ShipmentWithItemsAndAdditionalFields } from '~shared/features/shipments/types';
import { useDispatchShared } from '~shared/store';
import { useOpenTrackingLink } from '../../../../delivery-overview';
import { MenuOptions } from '../MenuOptions';
import { EditDeliveryActionSheet } from './EditDeliveryActionSheet';

type Props = {
  readonly shipment: ShipmentWithItemsAndAdditionalFields;
  readonly deliveredAt?: string; // used when deliveries first enabled
};

export const UpcomingDeliveryActionSheet = ({ shipment, deliveredAt }: Props) => {
  const { navigate } = useNavigation();
  const { openTrackingLink } = useOpenTrackingLink();

  const dispatch = useDispatchShared();

  useEffect(() => {
    dispatch(sendAssistantViewedEvent(ASSISTANT_VIEWED_EVENTS.UPCOMING_DELIVERY_ACTION_SHEET));
  }, [dispatch]);

  const { saveStack, setActiveActionSheet, closeActionSheet } = useContext(ActionSheetContext);

  const isEditable = isShipmentEditable(shipment.status);

  const onBeforeNavigate = (action: AssistantDeliveryAction) => {
    closeActionSheet();
    dispatch(sendAssistantUpcomingDeliveryActionChosen({ action, editable: isEditable, shipmentID: shipment.id }));
  };

  const navigateToMessageForm = (params: { subject: AssistantMessageSubject; category: AssistantCategory }) => {
    saveStack<ActionSheetProviderStackCacheKey>('assistant');
    navigate('RouteAssistantMessage', {
      origin: ASSISTANT_CHAT_ORIGINS.ALTO_ASSISTANT_CHOOSE_DELIVERY_ACTION_MENU,
      messagePrompt: MESSAGE_PROMPTS.LET_US_HELP,
      ...params,
    });
  };

  const options = [
    {
      iconName: 'truck-duo' as const,
      text: 'Track my delivery',
      description: shipment.tracking_link,
      onPress: () => {
        dispatch(
          sendAssistantUpcomingDeliveryActionChosen({
            action: ASSISTANT_DELIVERY_ACTIONS.TRACK_MY_DELIVERY,
            editable: isEditable,
            shipmentID: shipment.id,
          }),
        );
        openTrackingLink(shipment.tracking_link ?? '');
      },
      hide: !shipment.tracking_link,
    },
    {
      iconName: 'cancel-duo' as const,
      text: 'Cancel delivery',
      onPress: () => {
        onBeforeNavigate(ASSISTANT_DELIVERY_ACTIONS.CANCEL_ORDER);
        if (isEditable) {
          saveStack<ActionSheetProviderStackCacheKey>('assistant-cancel-order');
          navigate('RouteDeliveryDetails', { shipmentID: shipment.id });
        } else {
          navigateToMessageForm({
            subject: ASSISTANT_MESSAGE_SUBJECTS.CANCEL_DELIVERY,
            category: 'deliveries.cancel_order',
          });
        }
      },
    },
    {
      iconName: 'edit-duo' as const,
      text: 'Edit delivery',
      onPress: () => {
        dispatch(
          sendAssistantUpcomingDeliveryActionChosen({
            action: ASSISTANT_DELIVERY_ACTIONS.EDIT_DELIVERY,
            editable: isEditable,
            shipmentID: shipment.id,
          }),
        );
        setActiveActionSheet(
          <EditDeliveryActionSheet
            address={shipment.address}
            date={shipment.date}
            deliverAfter={shipment.deliver_after}
            deliverBefore={shipment.deliver_before}
            items={shipment.items}
            shipmentID={shipment.id}
          />,
        );
      },
    },
    {
      iconName: 'pricetag-duo' as const,
      text: 'Pricing question',
      onPress: () => {
        onBeforeNavigate(ASSISTANT_DELIVERY_ACTIONS.PRICING_QUESTION);
        navigate('RouteUpcomingDeliveryPriceDetails', { shipmentID: shipment.id });
      },
    },
    {
      iconName: 'questionmark-duo' as const,
      text: 'Something else',
      onPress: () => {
        onBeforeNavigate(ASSISTANT_DELIVERY_ACTIONS.SOMETHING_ELSE);
        navigateToMessageForm({
          subject: ASSISTANT_MESSAGE_SUBJECTS.UPCOMING_DELIVERY,
          category: 'deliveries.something_else',
        });
      },
    },
  ];

  return (
    <ActionSheetV2
      title="Upcoming delivery"
      description={deliveredAt || getItemMedNames(shipment.items) || ''}
      analyticsName="assistant upcoming delivery"
    >
      <MenuOptions options={options} />
    </ActionSheetV2>
  );
};
